import { CORE_POST_BATTLE_FIELDS, CORE_POST_POLL_FIELDS } from "./queries/postQueries";
import { CHAT_GAME_PREVIEW_FIELDS } from "./queries/chatGameQueries";
/**
 * GraphQL documents targeting **Core** (`/api/core/graphql/`). Legacy export names kept for incremental wiring.
 */

/** Same shape as `engagingFeed` node — returned from `websiteUpdatePost`. */
export const WEBSITE_MUTATION_UPDATE_POST = `
  mutation WebsiteUpdatePost(
    $postId: Int!
    $postText: String
    $visibility: String
    $background: Int
    $layout: String
    $teamId: Int
  ) {
    websiteUpdatePost: updatePost(
      postId: $postId
      text: $postText
      visibility: $visibility
      background: $background
      layout: $layout
      teamId: $teamId
    ) {
      ok
      error
      post {
        id
        text
        code
        visibility
        background
        layout
        ${CORE_POST_POLL_FIELDS}
        ${CORE_POST_BATTLE_FIELDS}
        isEdited
        isPinned
        authorId
        teamId
        sharedFromId
        createdAtText
        commentsCount
        reactionsCount
        savedByMe
        reactedByMe
        myReaction
        topReactions
        reactorName
        author {
          userId
          username
          fullName
          profile {
            avatarUrl
          }
        }
        media(first: 20) {
          edges {
            node {
              url
              thumbnailUrl
              mediaType
            }
          }
        }
      }
    }
  }
`;

export const WEBSITE_MUTATION_DELETE_POST = `
  mutation WebsiteDeletePost($postId: Int!) {
    websiteDeletePost: deletePost(postId: $postId) {
      ok
      error
    }
  }
`;

/** Text/code posts only — Core has no Upload on createPost (attach media separately when backend supports it). */
export const WEBSITE_MUTATION_CREATE_POST = `
  mutation WebsiteCreatePost(
    $postText: String
    $visibility: String!
    $background: Int!
    $layout: String
    $postCode: String
    $sharedFromId: Int
    $teamId: Int
  ) {
    websiteCreatePost: createPost(
      text: $postText
      visibility: $visibility
      background: $background
      layout: $layout
      code: $postCode
      sharedFromId: $sharedFromId
      teamId: $teamId
    ) {
      ok
      error
      post {
        id
        text
        code
        visibility
        background
        layout
        ${CORE_POST_POLL_FIELDS}
        ${CORE_POST_BATTLE_FIELDS}
        isEdited
        isPinned
        authorId
        teamId
        sharedFromId
        createdAtText
        commentsCount
        reactionsCount
        savedByMe
        reactedByMe
        myReaction
        topReactions
        reactorName
        author {
          userId
          username
          fullName
          profile {
            avatarUrl
          }
        }
        media(first: 20) {
          edges {
            node {
              url
              thumbnailUrl
              mediaType
            }
          }
        }
      }
    }
  }
`;

/** Core createPostComment — text and/or one attachment (photo, voice note). */
export const WEBSITE_MUTATION_CREATE_COMMENT = `
  mutation WebsiteCreateComment($postId: Int!, $commentText: String, $file: Upload) {
    websiteCreateComment: createPostComment(postId: $postId, text: $commentText, file: $file) {
      ok
      error
      comment {
        id
        text
        createdAtText
        repliesCount
        reactionsCount
        isPinned
        byPostAuthor
        fileUrl
        fileName
        user {
          userId
          username
          fullName
          profile {
            avatarUrl
          }
        }
        post {
          id
          commentsCount
        }
      }
    }
  }
`;

/** Reply — text and/or one attachment; `replyToId` is the reply being answered in the thread. */
export const WEBSITE_MUTATION_CREATE_COMMENT_REPLY = `
  mutation WebsiteCreateCommentReply(
    $commentId: Int!
    $replayText: String
    $file: Upload
    $replyToId: Int
  ) {
    websiteCreateCommentReply: createCommentReply(
      commentId: $commentId
      text: $replayText
      file: $file
      replyToId: $replyToId
    ) {
      ok
      error
      reply {
        id
        text
        createdAtText
        fileUrl
        fileName
        byPostAuthor
        replyToUser {
          userId
          username
          fullName
        }
        user {
          userId
          username
          fullName
          profile {
            avatarUrl
          }
        }
        comment {
          id
          repliesCount
        }
      }
    }
  }
`;

/**
 * Post thread — Relay pagination. `sort: "newest"` pages back with `last`/`before`;
 * `sort: "top"` (pinned first, then most engaging) pages forward with `first`/`after`.
 */
export const WEBSITE_QUERY_POST_COMMENTS = `
  query WebsitePostComments(
    $postId: Int!
    $sort: String
    $first: Int
    $last: Int
    $after: String
    $before: String
  ) {
    postComments(
      postId: $postId
      sort: $sort
      first: $first
      last: $last
      after: $after
      before: $before
    ) {
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          text
          isEdited
          createdAtText
          repliesCount
          reactionsCount
          reactedByMe
          myReaction
          topReactions
          isPinned
          byPostAuthor
          fileUrl
          fileName
          recentRepliers {
            userId
            username
            fullName
            profile {
              avatarUrl
            }
          }
          user {
            userId
            username
            fullName
            profile {
              avatarUrl
              isOnline
            }
          }
        }
      }
    }
  }
`;

/** Single comment by id — replaces GET `/api/getComment/?commentid=`. Reply lists use `WEBSITE_QUERY_COMMENT_REPLIES`. */
export const WEBSITE_QUERY_COMMENT_BY_ID = `
  query WebsiteCommentById($commentId: Int!) {
    commentById(commentId: $commentId) {
      id
      text
      isEdited
      createdAtText
      repliesCount
      reactionsCount
      reactedByMe
      myReaction
      topReactions
      isPinned
      byPostAuthor
      fileUrl
      fileName
      user {
        userId
        username
        fullName
        profile {
          avatarUrl
          isOnline
        }
      }
    }
  }
`;

/** Comment replies — Relay (`last`/`before` = older); replaces GET `/api/commentsreplay/?commentid=`. */
export const WEBSITE_QUERY_COMMENT_REPLIES = `
  query WebsiteCommentReplies(
    $commentId: Int!
    $first: Int
    $last: Int
    $after: String
    $before: String
  ) {
    commentReplies(
      commentId: $commentId
      first: $first
      last: $last
      after: $after
      before: $before
    ) {
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          text
          isEdited
          createdAtText
          fileUrl
          fileName
          byPostAuthor
          replyToUser {
            userId
            username
            fullName
          }
          user {
            userId
            username
            fullName
            profile {
              avatarUrl
              isOnline
            }
          }
        }
      }
    }
  }
`;

/** Post likes — Relay (`last`/`before` = older); replaces GET `/api/likes/?postid=`. */
export const WEBSITE_QUERY_POST_LIKES = `
  query WebsitePostLikes(
    $postId: Int!
    $first: Int
    $last: Int
    $after: String
    $before: String
  ) {
    postLikes: postReactions(
      postId: $postId
      first: $first
      last: $last
      after: $after
      before: $before
    ) {
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          reaction
          createdAtText
          user {
            userId
            username
            fullName
            profile {
              avatarUrl
            }
          }
        }
      }
    }
  }
`;

export const WEBSITE_QUERY_VIEWER_AND_PROFILE = `
  query WebsiteViewerAndProfile {
    viewer: me {
      userId
      username
      email
      firstName
      lastName
      fullName
      isOwner
    }
    myProfile {
      bio
      country
      language
      avatarUrl
      coverUrl
      skills
      jobTitle
      isOnline
      lastSeenText
      isVerified
      sticker
      onboardingDone
    }
    myProfilePrivacy {
      showActivityStatus
      mentionPermission
      messagePrivacy
    }
    myFollowing: following(first: 1) {
      totalCount
    }
    myFollowers: followers(first: 1) {
      totalCount
    }
  }
`;

/**
 * Core `updateMyProfile` — matches `core_api/graphql/mutations_profile.py`.
 * Core also accepts firstName / lastName here (the mobile app sends them); the website keeps names read-only.
 */
export const WEBSITE_MUTATION_UPDATE_MY_PROFILE = `
  mutation WebsiteUpdateMyProfile(
    $phone: String
    $address: String
    $country: String
    $birthDate: Date
    $skills: String
    $jobTitle: String
    $bio: String
    $gender: String
    $websiteUrl: String
    $language: String
  ) {
    updateMyProfile(
      phone: $phone
      address: $address
      country: $country
      birthDate: $birthDate
      skills: $skills
      jobTitle: $jobTitle
      bio: $bio
      gender: $gender
      websiteUrl: $websiteUrl
      language: $language
    ) {
      ok
      error
      profile {
        username
        fullName
        bio
        phone
        address
        country
        skills
        jobTitle
        language
        avatarUrl
        coverUrl
        gender
        websiteUrl
        birthDateText
      }
    }
  }
`;

/** Core field visibility — maps to ``ProfilePrivacySetting`` (public / friends / private). */
export const WEBSITE_MUTATION_UPDATE_MY_PROFILE_PRIVACY = `
  mutation WebsiteUpdateMyProfilePrivacy(
    $gender: String
    $address: String
    $country: String
    $birthDate: String
    $phone: String
    $showActivityStatus: Boolean
    $mentionPermission: String
  ) {
    updateMyProfilePrivacy(
      gender: $gender
      address: $address
      country: $country
      birthDate: $birthDate
      phone: $phone
      showActivityStatus: $showActivityStatus
      mentionPermission: $mentionPermission
    ) {
      ok
      error
      privacy {
        genderVisibility
        addressVisibility
        countryVisibility
        birthDateVisibility
        phoneVisibility
        showActivityStatus
        mentionPermission
      }
    }
  }
`;

/** Viewer-authored posts — Core `myPosts` Relay connection (no legacy `myRecentPosts`). */
export const WEBSITE_QUERY_MY_RECENT_POSTS = `
  query WebsiteMyRecentPosts($first: Int!, $after: String) {
    myPosts(first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          text
          createdAtText
          media(first: 1) {
            edges {
              node {
                mediaType
              }
            }
          }
        }
      }
    }
  }
`;

/** Replaced by `graphql/queries/profileQueries.js` (`CORE_QUERY_PROFILE_*`) — keep export name for grep migrations. */
export const WEBSITE_QUERY_USER_DATA = `
  query WebsiteUserData_DEPRECATED {
    __typename
  }
`;

/**
 * Core GraphQL exposes ``myNotifications``; list fetching lives in
 * ``main/notificationsGraphql.js`` (query in ``graphql/queries/coreNotificationsQueries.js``).
 * This document remains a valid no-op so accidental imports do not hit Core with invalid fields.
 */
export const WEBSITE_QUERY_NOTIFICATIONS = `
  query WebsiteNotificationsCoreStub {
    __typename
  }
`;

/** قائمة محادثات فردية — Core `directChats`. */
export const WEBSITE_QUERY_CHAT_USERS = `
  query WebsiteChatUsers($first: Int!, $after: String) {
    me {
      userId
    }
    chatUsers: directChats(first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          updatedAtText
          lastMessage {
            id
            text
            senderId
            senderUsername
            senderFullName
            createdAtText
            viewOnce { kind isOpened }
            game { ${CHAT_GAME_PREVIEW_FIELDS} }
          }
          participants {
            userId
            username
            fullName
            avatarUrl
            isOnline
          }
          canMessage
          cannotMessageReason
          myState {
            isRead
            isNew
            isSeen
          }
        }
      }
    }
  }
`;

/** إرسال رسالة DM — Core ``sendDirectMessage`` (نص و/أو ملف عبر multipart). */
export const WEBSITE_MUTATION_SEND_DM_MESSAGE = `
  mutation WebsiteSendDmMessage($receiverUsername: String!, $text: String, $file: Upload, $replyToId: Int) {
    sendDirectMessage(receiverUsername: $receiverUsername, text: $text, file: $file, replyToId: $replyToId) {
      ok
      error
      message {
        id
        text
        messageType
        fileUrl
        fileName
        fileSize
        fileContentType
        isImage
        isAudio
        createdAtText
        isDeleted
        senderId
        senderUsername
        senderFullName
        senderAvatarUrl
        replyTo {
          id
          text
          messageType
          fileUrl
          fileName
          fileContentType
          isImage
          isAudio
          senderId
          senderUsername
          senderFullName
        }
        conversation {
          id
        }
      }
      conversation {
        id
      }
    }
  }
`;

/** ستوريز — لا يوجد ما يعادلها في Core بعد؛ انظر `currentStoriesGraphql.js`. */
export const WEBSITE_QUERY_CURRENT_STORIES = `
  query WebsiteCurrentStories_DEPRECATED {
    __typename
  }
`;

/** اقتراح أعضاء — استخدام اتصال `profiles` من Core كبديل تقريبي. */
export const WEBSITE_QUERY_SUGGESTED_FRIENDS = `
  query WebsiteSuggestedFriends($first: Int!, $after: String) {
    me {
      userId
    }
    suggestedFriends: profiles(first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          username
          fullName
          avatarUrl
          country
          skills
          jobTitle
          isVerified
          user {
            userId
          }
        }
      }
    }
  }
`;

/** اقتراح فرق — اتصال `teams`. */
export const WEBSITE_QUERY_SUGGESTED_TEAMS = `
  query WebsiteSuggestedTeams($first: Int!, $after: String) {
    suggestedTeams: teams(first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          name
          description
          corporation
          interests
          logoUrl
          owner {
            userId
          }
        }
      }
    }
  }
`;

/** Relay — المستخدمون المحظورون (REST `blocks/`). */
export const WEBSITE_QUERY_BLOCKED_USERS = `
  query WebsiteBlockedUsers($first: Int!, $after: String) {
    blockedUsers(first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          userId
          firstName
          lastName
          username
          fullName
          profile {
            avatarUrl
          }
        }
      }
    }
  }
`;

/** Relay connection — صفحة الأصدقاء كاملة (يشمل `infos`). */
export const WEBSITE_QUERY_USER_FRIENDS = `
  query WebsiteUserFriends($first: Int!, $after: String) {
    userFriends: friends(first: $first, after: $after) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          userId
          firstName
          lastName
          username
          fullName
          lastLogin
          profile {
            avatarUrl
            isOnline
          }
        }
      }
    }
  }
`;

/** نفس الاتصال بدون `infos` — للشريط الجانبي فقط لتقليل حجم الاستجابة. */
export const WEBSITE_QUERY_USER_FRIENDS_SIDEBAR = `
  query WebsiteUserFriendsSidebar($first: Int!, $after: String) {
    userFriends: friends(first: $first, after: $after) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          userId
          firstName
          lastName
          username
          fullName
          lastLogin
          profile {
            avatarUrl
            isOnline
          }
        }
      }
    }
  }
`;

/** Relay connection — طلبات صداقة واردة (REST `anotherReq/`). */
export const WEBSITE_QUERY_INCOMING_FRIEND_REQUESTS = `
  query WebsiteIncomingFriendRequests($first: Int!, $after: String) {
    incomingFriendRequests(first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          sender {
            userId
            username
            fullName
            mutualFriends
            profile {
              avatarUrl
              jobTitle
            }
          }
        }
      }
    }
  }
`;

/** Relay connection — طلبات صداقة أرسلها المستخدم (REST `friendrequests/`). */
export const WEBSITE_QUERY_OUTGOING_FRIEND_REQUESTS = `
  query WebsiteOutgoingFriendRequests($first: Int!, $after: String) {
    outgoingFriendRequests(first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          receiver {
            userId
            username
            fullName
            mutualFriends
            profile {
              avatarUrl
              jobTitle
            }
          }
        }
      }
    }
  }
`;

/** طلبات انضمام للفرق أرسلها المستخدم — Core `myTeamJoinRequests`. */
export const WEBSITE_QUERY_TEAM_JOIN_REQUESTS = `
  query WebsiteTeamJoinRequests($first: Int!, $after: String) {
    teamJoinRequests: myTeamJoinRequests(first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          status
          createdAtText
          team {
            id
            name
            description
            corporation
            logoUrl
          }
          user {
            userId
          }
        }
      }
    }
  }
`;

/** فرق انضمّ إليها المستخدم — Core `myTeams`. */
export const WEBSITE_QUERY_JOINED_TEAMS = `
  query WebsiteJoinedTeams($first: Int!, $after: String) {
    joinedTeams: myTeams(first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          name
          description
          corporation
          interests
          logoUrl
          establishedAt
          createdAtText
          owner {
            userId
          }
        }
      }
    }
  }
`;

/** فرق يملكها المستخدم — يُفلتر على الواجهة من اتصال `teams`. */
export const WEBSITE_QUERY_USER_TEAMS = `
  query WebsiteUserTeams($first: Int!, $after: String) {
    me {
      userId
    }
    userTeams: teams(first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          name
          description
          corporation
          interests
          logoUrl
          establishedAt
          createdAtText
          owner {
            userId
          }
        }
      }
    }
  }
`;

/** اتجاهات — Core `trendingHashtags`. */
export const WEBSITE_QUERY_TRENDS = `
  query WebsiteTrends($first: Int!, $after: String, $days: Int) {
    trends: trendingHashtags(first: $first, after: $after, days: $days) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          name
          normalizedName
          createdAtText
        }
      }
    }
  }
`;

/** بحث مستخدمين — Relay `searchUsers`. */
export const WEBSITE_QUERY_SEARCH_USERS = `
  query SearchUsers($query: String!, $first: Int, $after: String) {
    searchUsers(query: $query, first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          userId
          username
          firstName
          lastName
          fullName
          profile {
            avatarUrl
          }
        }
      }
    }
  }
`;
